import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import useModules from "../hooks/useModules";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import InfoTooltip from "./InfoTooltip";
import DateInput from "./DateInput";

const EMPTY = {
    module_id: "",
    job_role: "",
    worker_category: "casual",
    hourly_rate: "",
    daily_rate: "",
    weekly_rate: "",
    biweekly_rate: "",
    monthly_rate: "",
    payment_frequency: "daily",
    currency: "USD",
    effective_date: "",
    employee_id: "",
};

const EMP_NONE = "__none__";

export default function WageRateFormDialog({ open, onOpenChange, onSaved, initial, icEmployees, isICScope, defaultModuleId }) {
    const { modules } = useModules();
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const isEdit = !!(initial && initial.id);

    useEffect(() => {
        if (!open) return;
        if (initial) {
            setForm({
                ...EMPTY,
                module_id: initial.module_id || defaultModuleId || "",
                job_role: initial.job_role || "",
                worker_category: initial.worker_category || "casual",
                hourly_rate: initial.hourly_rate ?? "",
                daily_rate: initial.daily_rate ?? "",
                weekly_rate: initial.weekly_rate ?? "",
                biweekly_rate: initial.biweekly_rate ?? "",
                monthly_rate: initial.monthly_rate ?? "",
                payment_frequency: initial.payment_frequency || "daily",
                currency: initial.currency || "USD",
                effective_date: initial.effective_date || "",
                employee_id: initial.employee_id || "",
            });
        } else {
            setForm({ ...EMPTY, module_id: defaultModuleId || "" });
        }
    }, [open, initial, defaultModuleId]);

    // Prefill rates from the most recent (approved or inactive) entry for the same triple
    useEffect(() => {
        if (!open) return;
        if (!form.module_id || !form.job_role || !form.worker_category) return;
        let alive = true;
        api.get("/wage-rates/history", {
            params: { module_id: form.module_id, worker_category: form.worker_category },
        }).then(({ data }) => {
            if (!alive) return;
            const role = form.job_role.trim().toLowerCase();
            const matches = (data || [])
                .filter((r) => (r.job_role || "").trim().toLowerCase() === role)
                .filter((r) => ["approved", "inactive", "superseded"].includes(r.status));
            if (matches.length === 0) return;
            matches.sort((a, b) => (b.effective_date || "").localeCompare(a.effective_date || ""));
            const latest = matches[0];
            setForm((f) => ({
                ...f,
                hourly_rate: latest.hourly_rate ?? f.hourly_rate,
                daily_rate: latest.daily_rate ?? f.daily_rate,
                weekly_rate: latest.weekly_rate ?? f.weekly_rate,
                biweekly_rate: latest.biweekly_rate ?? f.biweekly_rate,
                monthly_rate: latest.monthly_rate ?? f.monthly_rate,
                payment_frequency: latest.payment_frequency || f.payment_frequency,
                _prefilled_from: latest.effective_date,
            }));
        }).catch(() => { /* silent */ });
        return () => { alive = false; };
    }, [open, form.module_id, form.job_role, form.worker_category]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const payload = {
            ...form,
            hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : null,
            daily_rate: form.daily_rate ? Number(form.daily_rate) : null,
            weekly_rate: form.weekly_rate ? Number(form.weekly_rate) : null,
            biweekly_rate: form.biweekly_rate ? Number(form.biweekly_rate) : null,
            monthly_rate: form.monthly_rate ? Number(form.monthly_rate) : null,
            employee_id: form.employee_id && form.employee_id !== EMP_NONE ? form.employee_id : null,
        };
        // Discard the internal prefill breadcrumb before shipping to the API.
        delete payload._prefilled_from;
        if (!payload.hourly_rate && !payload.daily_rate && !payload.weekly_rate && !payload.biweekly_rate && !payload.monthly_rate) {
            toast.error("Provide at least one rate (hourly, daily, or monthly).");
            setSubmitting(false);
            return;
        }
        try {
            if (isEdit) {
                // PATCH — omit module_id (immutable via patch).
                const patchBody = { ...payload };
                delete patchBody.module_id;
                await api.patch(`/wage-rates/${initial.id}`, patchBody);
                toast.success("Wage rate updated");
            } else {
                await api.post("/wage-rates", payload);
                toast.success("Wage rate created");
            }
            onOpenChange(false);
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto"
                data-testid="wage-rate-form-dialog"
            >
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit Wage Rate" : "New Wage Rate"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4 pt-2">
                    {form._prefilled_from && !isEdit && (
                        <div className="text-[11px] asl-navy-text bg-[color:var(--asl-navy-50)] px-3 py-1.5 rounded" data-testid="wage-prefill-note">
                            Pre-filled from previous rate (effective {form._prefilled_from}). Edit to update.
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <FieldRow label="Module" required>
                            {/* Iter60.p13 — when created from inside a module
                                workspace `defaultModuleId` is set. Lock the field
                                so users can't accidentally switch modules.
                                Editing (`isEdit`) also locks per prior rule
                                (module is immutable after creation). */}
                            <SelectField
                                value={form.module_id}
                                onChange={(v) => set("module_id", v)}
                                items={modules.map((m) => ({ value: m.id, label: m.name }))}
                                testid="wage-module"
                                disabled={isEdit || !!defaultModuleId}
                            />
                            {(isEdit || !!defaultModuleId) && (
                                <div className="text-[10px] text-[color:var(--asl-muted)] mt-1" data-testid="wage-module-locked-hint">
                                    {isEdit ? "Module is immutable after creation." : "Locked to current module workspace."}
                                </div>
                            )}
                        </FieldRow>
                        <FieldRow label="Job role" required>
                            <Input
                                required
                                value={form.job_role}
                                onChange={(e) => set("job_role", e.target.value)}
                                data-testid="wage-job-role"
                            />
                        </FieldRow>
                        <FieldRow label="Worker category" required>
                            <SelectField
                                value={form.worker_category}
                                onChange={(v) => set("worker_category", v)}
                                items={[
                                    { value: "casual", label: "Casual" },
                                    {
                                        value: "non_casual_manual",
                                        label: "Non-Casual · Manual",
                                    },
                                    {
                                        value: "non_casual_non_manual",
                                        label: "Non-Casual · Non-Manual",
                                    },
                                ]}
                                testid="wage-category"
                            />
                        </FieldRow>
                        {/* Iter52d — Per-employee assignment available on all modules
                            when a specific module is scoped. */}
                        {isICScope && icEmployees && (
                            <FieldRow
                                label="Assign to specific employee (optional)"
                                tooltip="Leave blank to apply this rate to anyone matching the role + category. Pick a person to make this a person-specific override."
                            >
                                <SelectField
                                    value={form.employee_id || EMP_NONE}
                                    onChange={(v) => set("employee_id", v === EMP_NONE ? "" : v)}
                                    items={[
                                        { value: EMP_NONE, label: "— All matching employees —" },
                                        ...icEmployees.map((e) => ({ value: e.id, label: `${e.full_name} (${e.employee_code})` })),
                                    ]}
                                    testid="wage-employee"
                                />
                            </FieldRow>
                        )}
                        <FieldRow label="Payment frequency" required tooltip="How often this rate is paid out: hourly, daily wages, weekly, fortnightly, or monthly salary.">
                            <SelectField
                                value={form.payment_frequency}
                                onChange={(v) => set("payment_frequency", v)}
                                items={[
                                    { value: "hourly", label: "Hourly" },
                                    { value: "daily", label: "Daily" },
                                    { value: "weekly", label: "Weekly" },
                                    { value: "biweekly", label: "Bi-Weekly" },
                                    { value: "monthly", label: "Monthly" },
                                ]}
                                testid="wage-pay-freq"
                            />
                        </FieldRow>
                        <FieldRow label="Hourly rate (USD)">
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.hourly_rate}
                                onChange={(e) => set("hourly_rate", e.target.value)}
                                data-testid="wage-hourly"
                            />
                        </FieldRow>
                        <FieldRow label="Daily rate (USD)">
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.daily_rate}
                                onChange={(e) => set("daily_rate", e.target.value)}
                                data-testid="wage-daily"
                            />
                        </FieldRow>
                        <FieldRow label="Weekly rate (USD)">
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.weekly_rate}
                                onChange={(e) => set("weekly_rate", e.target.value)}
                                data-testid="wage-weekly"
                            />
                        </FieldRow>
                        <FieldRow label="Bi-Weekly rate (USD)">
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.biweekly_rate}
                                onChange={(e) => set("biweekly_rate", e.target.value)}
                                data-testid="wage-biweekly"
                            />
                        </FieldRow>
                        <FieldRow label="Monthly rate (USD)">
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.monthly_rate}
                                onChange={(e) => set("monthly_rate", e.target.value)}
                                data-testid="wage-monthly"
                            />
                        </FieldRow>
                        <FieldRow label="Effective date" required>
                            <DateInput
                                required
                                value={form.effective_date}
                                onChange={(v) => set("effective_date", v)}
                                data-testid="wage-effective-date"
                            />
                        </FieldRow>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="wage-form-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="asl-navy-bg text-white"
                            disabled={submitting}
                            data-testid="wage-form-submit"
                        >
                            {submitting ? "Saving…" : (isEdit ? "Save changes" : "Create wage rate")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function FieldRow({ label, required, tooltip, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[color:var(--asl-muted)] flex items-center">
                {label} {required && <span className="asl-crimson-text">*</span>}
                {tooltip && <InfoTooltip text={tooltip} />}
            </Label>
            {children}
        </div>
    );
}

function SelectField({ value, onChange, items, testid, disabled }) {
    return (
        <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger data-testid={testid}>
                <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
                {items.map((i) => (
                    <SelectItem key={i.value} value={i.value} data-testid={`${testid}-opt-${i.value}`}>
                        {i.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
